/**
 * Iter88zax (2026-02-19) — Super-Admin: Membership-Margen Diagnose-View.
 *
 * Listet ALLE User mit ihren Memberships und Marge-Werten. Inkonsistente
 * Datensätze (User-Doc null aber Membership 18 etc.) sind rot markiert.
 * Super-Admin kann per Zeile eine einzelne Membership-Marge korrigieren.
 */
import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, ArrowLeft, Save, X, Search, RefreshCcw } from "lucide-react";
import api from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";

const fmt = (m) => m ? (m.type === "percent" ? `${m.value} %` : `${m.value} €`) : "—";

export default function AdminMembershipMargins() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [onlyInconsistent, setOnlyInconsistent] = useState(true);
  const [editing, setEditing] = useState(null); // {user_id, tenant_id, tenant_name, current_value}
  const [editVal, setEditVal] = useState("");
  const [editType, setEditType] = useState("percent");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/membership-margins");
      setRows(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Laden fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let out = rows;
    if (onlyInconsistent) out = out.filter((u) => u.has_inconsistency);
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      out = out.filter((u) =>
        (u.email || "").toLowerCase().includes(s)
        || (u.name || "").toLowerCase().includes(s)
        || u.memberships.some((m) => (m.tenant_name || "").toLowerCase().includes(s))
      );
    }
    return out;
  }, [rows, q, onlyInconsistent]);

  const startEdit = (userId, row) => {
    setEditing({ user_id: userId, tenant_id: row.tenant_id, tenant_name: row.tenant_name });
    setEditVal(row.membership_margin?.value ?? "");
    setEditType(row.membership_margin?.type ?? "percent");
  };
  const saveEdit = async () => {
    if (!editing) return;
    setBusy(true);
    try {
      const payload = {
        margin_type: editVal === "" ? null : editType,
        margin_value: editVal === "" ? null : Number(editVal),
      };
      await api.post(`/admin/membership-margins/${editing.user_id}/${editing.tenant_id}`, payload);
      toast.success("Membership-Marge aktualisiert");
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  const inconsistentCount = rows.filter((u) => u.has_inconsistency).length;

  return (
    <div className="min-h-screen bg-[#09090B] text-zinc-100">
      <AppHeader />
      <div className="max-w-6xl mx-auto px-4 py-6">
        <Link to="/dashboard" className="inline-flex items-center gap-1 text-sm text-zinc-400 hover:text-white mb-4">
          <ArrowLeft size={14} /> Zurück
        </Link>
        <h1 className="font-display text-2xl font-semibold mb-1">Membership-Marge Diagnose</h1>
        <p className="text-sm text-zinc-400 mb-6">
          Super-Admin Werkzeug zur Prüfung und Korrektur von Marge-Inkonsistenzen zwischen User-Datensatz und Memberships.
          {inconsistentCount > 0 && <span className="ml-1 font-bold text-amber-400">{inconsistentCount} User haben Inkonsistenzen.</span>}
        </p>

        <div className="mb-4 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Suche nach Name, Email oder Mandant …"
              data-testid="search-membership"
              className="w-full h-10 pl-9 pr-3 rounded-lg bg-[#18181B] border border-zinc-800 text-sm"
            />
          </div>
          <label className="flex items-center gap-1.5 text-[12px] text-zinc-300 cursor-pointer">
            <input
              type="checkbox"
              checked={onlyInconsistent}
              onChange={(e) => setOnlyInconsistent(e.target.checked)}
              data-testid="only-inconsistent-toggle"
              className="h-4 w-4 rounded border-zinc-600 bg-[#0F1418] text-amber-500 focus:ring-amber-500"
            />
            Nur Inkonsistenzen
          </label>
          <button onClick={load} className="h-10 px-3 rounded-lg bg-zinc-800 border border-zinc-700 text-zinc-200 text-[12px] inline-flex items-center gap-1.5 hover:bg-zinc-700">
            <RefreshCcw size={13} /> Neu laden
          </button>
        </div>

        {loading ? (
          <div className="text-center py-16 text-zinc-500">Lade…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-zinc-500 bg-[#18181B] rounded-2xl border border-zinc-800">
            Keine Einträge {onlyInconsistent ? "mit Inkonsistenzen" : ""}.
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((u) => (
              <div
                key={u.user_id}
                data-testid={`user-row-${u.user_id}`}
                className={`bg-[#18181B] border rounded-2xl p-4 ${u.has_inconsistency ? "border-amber-500/40" : "border-zinc-800"}`}
              >
                <div className="flex items-start gap-3">
                  {u.has_inconsistency && (
                    <div className="mt-0.5 text-amber-400 shrink-0"><AlertTriangle size={16} /></div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <div className="font-semibold text-white">{u.name || u.email}</div>
                      {u.name && <div className="text-[11.5px] text-zinc-500">{u.email}</div>}
                      <div className="text-[10px] uppercase tracking-widest bg-zinc-800 text-zinc-300 px-1.5 py-0.5 rounded">
                        {u.role_global}
                      </div>
                      {u.is_super_admin && (
                        <div className="text-[10px] uppercase tracking-widest bg-purple-500/20 text-purple-300 px-1.5 py-0.5 rounded font-bold">Super-Admin</div>
                      )}
                    </div>
                    <div className="mt-1 text-[11.5px] text-zinc-400">
                      <span className="font-mono">User-Doc:</span> {fmt(u.user_doc_margin)}
                    </div>
                    <div className="mt-3 space-y-1.5">
                      {u.memberships.map((r) => {
                        const mm = r.membership_margin;
                        const um = u.user_doc_margin;
                        const bad = ((mm === null) !== (um === null)) || (mm && um && (mm.type !== um.type || mm.value !== um.value));
                        return (
                          <div key={r.tenant_id} className="flex items-center gap-2 flex-wrap">
                            <div className="text-[13px] font-medium text-zinc-200">{r.tenant_name}</div>
                            {r.is_primary && <span className="text-[9px] uppercase tracking-widest bg-emerald-500/20 text-emerald-300 px-1 rounded font-bold">Primary</span>}
                            {r._legacy_virtual && <span className="text-[9px] uppercase tracking-widest bg-orange-500/20 text-orange-300 px-1 rounded font-bold">Legacy</span>}
                            <span className={`font-mono text-[12px] px-1.5 py-0.5 rounded ${bad ? "bg-amber-500/20 text-amber-200" : "bg-zinc-800 text-zinc-300"}`}>
                              MS: {fmt(mm)}
                            </span>
                            <span className="font-mono text-[11px] text-zinc-500">
                              (Mandant-Std: {fmt(r.tenant_default)})
                            </span>
                            <span className="text-[10px] text-zinc-500">
                              Rolle: {r.role_in_tenant}
                            </span>
                            {!r._legacy_virtual && (
                              <button
                                onClick={() => startEdit(u.user_id, r)}
                                data-testid={`edit-membership-${u.user_id}-${r.tenant_id}`}
                                className="ml-auto text-[11px] text-emerald-400 hover:text-emerald-300 font-medium"
                              >
                                Bearbeiten
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5 max-w-md w-full">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-bold text-lg">Membership-Marge bearbeiten</h2>
              <button onClick={() => setEditing(null)} className="p-1 hover:bg-zinc-800 rounded"><X size={18} /></button>
            </div>
            <p className="text-[12px] text-zinc-400 mb-3">Mandant: <b className="text-white">{editing.tenant_name}</b></p>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <button
                onClick={() => setEditType("percent")}
                className={`h-10 rounded-lg font-semibold text-sm ${editType === "percent" ? "bg-emerald-500 text-black" : "bg-zinc-800 text-zinc-300"}`}
              >% Prozent</button>
              <button
                onClick={() => setEditType("eur")}
                className={`h-10 rounded-lg font-semibold text-sm ${editType === "eur" ? "bg-emerald-500 text-black" : "bg-zinc-800 text-zinc-300"}`}
              >€ Euro</button>
            </div>
            <input
              type="number"
              value={editVal}
              onChange={(e) => setEditVal(e.target.value)}
              placeholder={editType === "percent" ? "z.B. 15" : "z.B. 200"}
              data-testid="edit-margin-input"
              className="w-full h-11 px-3 rounded-lg bg-[#0F0F11] border border-zinc-700 text-sm"
            />
            <p className="text-[11px] text-zinc-500 mt-1.5">Leer lassen → Membership-Marge löschen (Vererbung zum Tenant-Standard).</p>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setEditing(null)} className="flex-1 h-10 rounded-lg bg-zinc-800 border border-zinc-700 text-zinc-200 font-medium">Abbrechen</button>
              <button
                onClick={saveEdit}
                disabled={busy}
                data-testid="save-membership-margin"
                className="flex-1 h-10 rounded-lg bg-emerald-500 text-black font-bold inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
              >
                <Save size={14} /> Speichern
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
